@if (data(); as d) {
  @let $editing = editing();

  <ng-template #headerEnd>
    @if (!$editing) {
      <calcite-action
        icon="pencil"
        scale="m"
        text="Edit"
        text-enabled
        (click)="startEdit()"
      />
    } @else {
      <calcite-action
        icon="check"
        scale="s"
        text="Save"
        text-enabled
        class="app-success"
        [disabled]="saving()"
        (click)="save()"
      />
      <calcite-action
        icon="x"
        scale="m"
        class="app-danger"
        text="Cancel"
        text-enabled
        [disabled]="saving()"
        (click)="cancel()"
      />
    }
  </ng-template>

  <section class="app-panel app-stack">
    <div class="app-header-row">
      <h2 class="app-text-h-sm">Assumptions</h2>
    </div>

    <div class="app-form-grid">
      <label class="app-field">
        <span>Vacancy Rate <span class="app-text-negative">*</span></span>
        @if ($editing) {
          <calcite-input-number
            suffix-text="%"
            placeholder="—"
            number-button-type="none"
            [value]="vacancyRateDisplay()"
            (input)="updateVacancyRate($any($event.target).value)"
          ></calcite-input-number>
        } @else {
          <span class="app-text-body">{{ (d.income_approach.vacancy_rate | percent:'1.0-2') ?? '—' }}</span>
        }
      </label>

      <label class="app-field">
        <span>Cap Rate <span class="app-text-negative">*</span></span>
        @if ($editing) {
          <calcite-input-number
            suffix-text="%"
            placeholder="—"
            number-button-type="none"
            [value]="capRateDisplay()"
            (input)="updateCapRate($any($event.target).value)"
          ></calcite-input-number>
        } @else {
          <span class="app-text-body">{{ (d.income_approach.cap_rate | percent:'1.0-2') ?? '—' }}</span>
        }
      </label>

      <label class="app-field">
        <span>Tax Rate</span>
        <span class="app-text-body">{{ taxRateDisplay() }}</span>
      </label>

      <label class="app-field">
        <span>Overall Rate</span>
        <span class="app-text-body">{{ (overallRateFraction() | percent:'1.0-2') ?? '—' }}</span>
      </label>

      <label class="app-field">
        <span>Expense Method <span class="app-text-negative">*</span></span>
        @if ($editing) {
          <calcite-segmented-control
            width="full"
            (calciteSegmentedControlChange)="updateExpenseMethod($any($event.target).value)"
          >
            <calcite-segmented-control-item
              value="per_unit"
              [checked]="currentExpenseMethod() === 'per_unit'"
            >Per Unit
            </calcite-segmented-control-item>
            <calcite-segmented-control-item
              value="pct_egai"
              [checked]="currentExpenseMethod() === 'pct_egai'"
            >% of EGAI
            </calcite-segmented-control-item>
          </calcite-segmented-control>
        } @else {
          <span class="app-text-body">{{ expenseMethodLabel() }}</span>
        }
      </label>

      <label class="app-field">
        <span>{{ expenseValueLabel() }} <span class="app-text-negative">*</span></span>
        @if ($editing) {
          @if (currentExpenseMethod() === 'per_unit') {
            <calcite-input-number
              prefix-text="$"
              [groupSeparator]="true"
              placeholder="—"
              number-button-type="none"
              [value]="expensePerUnitDisplay()"
              (input)="updateExpensePerUnit($any($event.target).value)"
            ></calcite-input-number>
          } @else {
            <calcite-input-number
              suffix-text="%"
              placeholder="—"
              number-button-type="none"
              [value]="expensePctDisplay()"
              (input)="updateExpensePct($any($event.target).value)"
            ></calcite-input-number>
          }
        } @else {
          @if (currentExpenseMethod() === 'per_unit') {
            <span
              class="app-text-body">{{ (d.income_approach.expense_per_unit | currency:'USD':'symbol':'1.0-0') ?? '—' }}</span>
          } @else {
            <span class="app-text-body">{{ (d.income_approach.expense_pct | percent:'1.0-2') ?? '—' }}</span>
          }
        }
      </label>

      @if ($editing || ((d.income_approach.remarks?.length ?? 0) > 0)) {
        <label class="app-field app-field-full">
          <span>Remarks</span>
          @if ($editing) {
            <calcite-text-area
              rows="2"
              [value]="remarksDisplay()"
              (input)="updateRemarks($any($event.target).value)"
            ></calcite-text-area>
          } @else {
            <span class="app-text-body ia-remarks-view">{{ d.income_approach.remarks }}</span>
          }
        </label>
      }
    </div>
  </section>

  @if (table(); as t) {
    <section class="app-panel app-stack">
      <h2 class="app-text-h-sm">Income Approach</h2>
      <div class="app-table-shell ia-table-shell">
        <table class="app-table-calcite app-table-nowrap">
          <thead>
          <tr>
            <th>Income Source</th>
            @for (col of t.columns; track col.key) {
              <th class="ia-num">{{ col.label }}</th>
            }
          </tr>
          </thead>
          <tbody>
          <tr>
            <td class="app-text-label"><strong>Units</strong></td>
            @for (cell of t.unitsCells; track $index) {
              <td class="ia-num" [class.app-muted]="cell === null">{{ (cell | number) ?? '—' }}</td>
            }
          </tr>
          <tr>
            <td class="app-text-label"><strong>Monthly Rent</strong> <span class="app-text-negative">*</span></td>
            @for (col of t.columns; track col.key) {
              <td class="ia-num" [class.app-muted]="col.groupIndex === null">
                @if (col.groupIndex === null) {
                  —
                } @else if ($editing) {
                  <calcite-input-number
                    prefix-text="$"
                    [groupSeparator]="true"
                    placeholder="—"
                    number-button-type="none"
                    [value]="monthlyRentDisplay(col.groupIndex)"
                    (input)="updateGroupRent(col.groupIndex, $any($event.target).value)"
                  ></calcite-input-number>
                } @else {
                  {{ (monthlyRentValue(col.groupIndex) | currency:'USD':'symbol':'1.0-0') ?? '—' }}
                }
              </td>
            }
          </tr>
            @for (metric of t.metricRows; track metric.label) {
              <tr>
                <td class="app-text-label">
                  <span class="ia-row-header">
                    <strong>{{ metric.label }}</strong>
                    @if (metricTooltip(metric.label); as tip) {
                      <calcite-icon
                        [id]="metricHelpId(metric.label)"
                        icon="question"
                        scale="s"
                        class="app-muted"
                      ></calcite-icon>
                      <calcite-tooltip [referenceElement]="metricHelpId(metric.label)">{{ tip }}</calcite-tooltip>
                    }
                  </span>
                </td>
                @for (col of t.columns; track col.key; let i = $index) {
                  @let cell = metric.cells[i];
                  <td class="ia-num"
                      [class.app-text-negative]="cell !== null && cell < 0"
                      [class.app-muted]="cell === null">
                    @if (metric.label === 'SGAI' && col.key === 'other-income' && $editing) {
                      <calcite-input-number
                        prefix-text="$"
                        [groupSeparator]="true"
                        placeholder="—"
                        number-button-type="none"
                        [value]="otherIncomeDisplay()"
                        (input)="updateOtherIncome($any($event.target).value)"
                      ></calcite-input-number>
                    } @else if (metric.label === 'SGAI' && col.key === 'direct-assessments' && $editing) {
                      <calcite-input-number
                        prefix-text="$"
                        [groupSeparator]="true"
                        placeholder="—"
                        number-button-type="none"
                        [value]="directAssessmentsDisplay()"
                        (input)="updateDirectAssessments($any($event.target).value)"
                      ></calcite-input-number>
                    } @else if (metric.label === 'Expenses' && col.key === 'direct-assessments' && $editing) {
                      <calcite-input-number
                        prefix-text="$"
                        [groupSeparator]="true"
                        placeholder="—"
                        number-button-type="none"
                        [value]="directAssessmentsExpenseDisplay()"
                        (input)="updateDirectAssessmentsExpense($any($event.target).value)"
                      ></calcite-input-number>
                    } @else {
                      @switch (metric.kind) {
                        @case ('percent') {
                          {{ (cell | percent:'1.0-2') ?? '—' }}
                        }
                        @default {
                          {{ (cell | currency:'USD':'symbol':'1.0-0') ?? '—' }}
                        }
                      }
                    }
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  } @else {
    <div class="app-callout">No income approach data available for this session.</div>
  }
}
